import { Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { errorMessage, get, post, type Me } from "../../lib/api";
import { useMe, usePublicConfig, useRefreshMe } from "../../lib/session";
import { Alert, Button, Card, Field, Input, Loading } from "../../components/ui";
import { Turnstile } from "../../components/Turnstile";

export function AuthLayout({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-center gap-2 text-lg font-semibold">
          <img src="/favicon.svg" alt="" className="h-8 w-8" /> Cloud Migration Hub
        </div>
        <Card>
          <h1 className="mb-5 text-xl font-semibold">{title}</h1>
          {children}
        </Card>
        {footer && <div className="muted mt-4 text-center text-sm">{footer}</div>}
      </div>
    </div>
  );
}

function home(me: Me): { to: string } {
  if (me.workspaces[0]) return { to: `/w/${me.workspaces[0].id}` };
  if (me.partners[0]) return { to: `/p/${me.partners[0].id}` };
  return { to: "/onboarding" };
}

export function RootRedirect() {
  const me = useMe();
  if (me.isLoading) return <Loading />;
  if (!me.data) return <Navigate to="/login" />;
  if (me.data.sessionState === "mfa_required") return <Navigate to="/mfa" />;
  if (me.data.sessionState !== "ok") return <Navigate to="/security" search={{ required: "1" }} />;
  return <Navigate {...home(me.data)} />;
}

export function LoginPage() {
  const search = useSearch({ strict: false }) as { next?: string };
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const onToken = useCallback((t: string | undefined) => setToken(t), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await post<Me & { mfaRequired?: boolean }>("/api/auth/login", { email, password, turnstileToken: token });
      await refresh();
      if (r.mfaRequired) navigate({ to: "/mfa", search: { next: search.next } });
      else if (search.next && search.next.startsWith("/")) window.location.assign(search.next);
      else navigate({ to: "/" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Sign in"
      footer={
        <>
          New here? <Link to="/signup" className="text-accent hover:underline">Create an account</Link>
        </>
      }
    >
      {error && <Alert tone="danger">{error}</Alert>}
      <form onSubmit={submit}>
        <Field label="Email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Password" htmlFor="password">
          <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Turnstile onToken={onToken} />
        <Button type="submit" className="w-full" loading={busy}>
          Sign in
        </Button>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/forgot" className="text-accent hover:underline">
          Forgot your password?
        </Link>
      </p>
    </AuthLayout>
  );
}

export function SignupPage() {
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const cfg = usePublicConfig();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const onToken = useCallback((t: string | undefined) => setToken(t), []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/signup", { ...form, turnstileToken: token });
      await refresh();
      navigate({ to: "/onboarding" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      footer={
        <>
          Already have an account? <Link to="/login" className="text-accent hover:underline">Sign in</Link>
        </>
      }
    >
      {cfg.data?.signupRestricted && <Alert tone="accent">Sign-up is invite-only on this environment.</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      <form onSubmit={submit}>
        <Field label="Your name" htmlFor="name">
          <Input id="name" autoComplete="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label="Work email" htmlFor="email">
          <Input id="email" type="email" autoComplete="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </Field>
        <Field label="Password" htmlFor="password" hint="At least 12 characters. A short phrase works well.">
          <Input id="password" type="password" autoComplete="new-password" minLength={12} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </Field>
        <Turnstile onToken={onToken} />
        <Button type="submit" className="w-full" loading={busy}>
          Create account
        </Button>
      </form>
      <p className="muted mt-4 text-xs">
        By creating an account you agree to the <a className="underline" href="https://cloudmigrationhub.com/terms">Terms</a> and{" "}
        <a className="underline" href="https://cloudmigrationhub.com/privacy">Privacy policy</a>.
      </p>
    </AuthLayout>
  );
}

export function MfaPage() {
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await post("/api/auth/login/mfa", useRecovery ? { recoveryCode: code } : { code });
      await refresh();
      navigate({ to: "/" });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title="Two-step verification">
      {error && <Alert tone="danger">{error}</Alert>}
      <form onSubmit={submit}>
        <Field label={useRecovery ? "Recovery code" : "6-digit code from your authenticator app"} htmlFor="code">
          <Input id="code" autoFocus autoComplete="one-time-code" inputMode={useRecovery ? "text" : "numeric"} required value={code} onChange={(e) => setCode(e.target.value)} />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Verify
        </Button>
      </form>
      <button className="text-accent mt-4 text-sm hover:underline" onClick={() => setUseRecovery((r) => !r)}>
        {useRecovery ? "Use an authenticator code instead" : "Use a recovery code"}
      </button>
    </AuthLayout>
  );
}

export function VerifyEmailPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const refresh = useRefreshMe();
  const [state, setState] = useState<"working" | "ok" | "error">("working");
  const [error, setError] = useState("");
  useEffect(() => {
    if (!token) return;
    post("/api/auth/verify-email", { token })
      .then(async () => {
        await refresh();
        setState("ok");
      })
      .catch((e) => {
        setError(errorMessage(e));
        setState("error");
      });
  }, [token, refresh]);
  return (
    <AuthLayout title="Verify your email">
      {!token && <Alert tone="danger">This link is missing its token.</Alert>}
      {token && state === "working" && <Loading label="Verifying…" />}
      {state === "ok" && (
        <>
          <Alert tone="ok">Your email address is verified.</Alert>
          <Link to="/" className="text-accent hover:underline">
            Continue
          </Link>
        </>
      )}
      {state === "error" && <Alert tone="danger">{error}</Alert>}
    </AuthLayout>
  );
}

export function ForgotPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title="Reset your password" footer={<Link to="/login" className="text-accent hover:underline">Back to sign in</Link>}>
      {sent ? (
        <Alert tone="ok">If an account exists for {email}, we've sent a reset link. It works once, for one hour.</Alert>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            await post("/api/auth/password/forgot", { email }).catch(() => undefined);
            setBusy(false);
            setSent(true);
          }}
        >
          <Field label="Email" htmlFor="email">
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={busy}>
            Send reset link
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [password, setPassword] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title="Choose a new password">
      {done ? (
        <>
          <Alert tone="ok">Your password has been changed. All other sessions were signed out.</Alert>
          <Link to="/login" className="text-accent hover:underline">
            Sign in
          </Link>
        </>
      ) : (
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setError(null);
            try {
              await post("/api/auth/password/reset", { token, password });
              setDone(true);
            } catch (err) {
              setError(errorMessage(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}
          <Field label="New password" htmlFor="pw" hint="At least 12 characters.">
            <Input id="pw" type="password" autoComplete="new-password" minLength={12} required value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Button type="submit" className="w-full" loading={busy}>
            Save password
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function InvitePage() {
  const { token } = useSearch({ strict: false }) as { token?: string };
  const me = useMe();
  const refresh = useRefreshMe();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (me.isLoading) return <Loading />;
  if (!me.data) return <Navigate to="/login" search={{ next: `/invite?token=${token ?? ""}` }} />;
  return (
    <AuthLayout title="Accept your invite">
      {error && <Alert tone="danger">{error}</Alert>}
      <p className="mb-4 text-sm">
        You're signed in as <b>{me.data.user.email}</b>.
      </p>
      <Button
        loading={busy}
        onClick={async () => {
          setBusy(true);
          setError(null);
          try {
            const r = await post<{ scopeType: string; scopeId: string }>("/api/invites/accept", { token });
            await refresh();
            navigate({ to: r.scopeType === "workspace" ? `/w/${r.scopeId}` : `/p/${r.scopeId}` });
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        Accept invite
      </Button>
    </AuthLayout>
  );
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const refresh = useRefreshMe();
  const [kind, setKind] = useState<"workspace" | "partner">("workspace");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title="Set up your account">
      <div className="mb-5 grid grid-cols-2 gap-2">
        {(["workspace", "partner"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`focus-ring rounded-lg border p-3 text-left text-sm ${kind === k ? "border-accent bg-accent-soft" : "border-default"}`}
            aria-pressed={kind === k}
          >
            <div className="font-medium">{k === "workspace" ? "A business" : "An agency or partner"}</div>
            <div className="muted mt-1 text-xs">{k === "workspace" ? "Migrate your own data." : "Run migrations for client workspaces and resell credits."}</div>
          </button>
        ))}
      </div>
      {error && <Alert tone="danger">{error}</Alert>}
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setError(null);
          try {
            const r = await post<{ id: string }>(kind === "workspace" ? "/api/workspaces" : "/api/partners", { name });
            await refresh();
            navigate({ to: kind === "workspace" ? `/w/${r.id}` : `/p/${r.id}` });
          } catch (err) {
            setError(errorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Field label={kind === "workspace" ? "Business name" : "Agency name"} htmlFor="name">
          <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button type="submit" className="w-full" loading={busy}>
          Continue
        </Button>
      </form>
    </AuthLayout>
  );
}

export function DevOutboxPage() {
  const [msgs, setMsgs] = useState<Array<{ id: string; to_addr: string; subject: string; text: string; created_at: number }> | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    get<{ messages: typeof msgs }>("/api/dev/outbox")
      .then((r) => setMsgs(r.messages))
      .catch((e) => setError(errorMessage(e)));
  }, []);
  return (
    <div className="mx-auto max-w-3xl p-6">
      <h1 className="mb-4 text-xl font-semibold">Dev outbox</h1>
      {error && <Alert tone="danger">{error}</Alert>}
      {msgs?.map((m) => (
        <Card key={m.id} className="mb-3" title={m.subject}>
          <div className="muted mb-2 text-xs">
            To {m.to_addr} · {new Date(m.created_at).toLocaleString()}
          </div>
          <pre className="text-sm whitespace-pre-wrap">
            {m.text.split(/(https?:\/\/\S+)/).map((part, i) =>
              /^https?:\/\//.test(part) ? (
                <a key={i} className="text-accent underline" href={part.replace(/^https?:\/\/[^/]+/, "")}>
                  {part}
                </a>
              ) : (
                part
              ),
            )}
          </pre>
        </Card>
      ))}
    </div>
  );
}
